import { Download, MoreHorizontal, Pencil, Upload } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { RichTextView } from '@/components/RichTextView'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { StatusBadge } from '@/components/ui/status-badge'
import { useConfirm } from '@/components/patterns/useConfirm'
import { PageHeader } from '@/features/preinvest/components/PageHeader'
import { runWithFeedback } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { CategoryChip } from '../components/CategoryChip'
import { exportRecords } from '../query/api'
import { archiveList, deleteListV2, duplicateList, listToStatic, unarchiveList } from './api'
import { listWhen } from './listDates'
import { ListDialog } from './ListDialog'
import type { ListDetail } from './types'

const contacts = (count: number): string => (count === 1 ? '1 contact' : `${count} contacten`)

/** A file name from the list name: letters and digits, the rest a dash. */
function fileName(name: string): string {
  const slug = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `lijst-${slug || 'export'}.xlsx`
}

/**
 * The head of a list page (spec-63 E5, design §4.7): the name with the actions, and under it what the list
 * is: kind, date or period, place, type, owner, description and, for a legacy filter, the warning. The
 * actions follow the type and the state: no Importeren on a dynamic or archived list, "Omzetten naar statisch"
 * only on a dynamic one, "Archiveren" or "Terugzetten" by state.
 */
export function ListHeader({ list, onChanged }: { list: ListDetail; onChanged: (list: ListDetail) => void }) {
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [dialog, confirm] = useConfirm()
  const archived = list.archived_at !== null
  const isStatic = list.type === 'static'
  const when = listWhen(list.starts_on, list.ends_on)

  async function exportMembers() {
    if (exporting) return
    setExporting(true)
    await runWithFeedback(
      () =>
        exportRecords(
          'contacten',
          { query: { version: 2, quick: [{ property: 'lists', operator: 'contains_any_of', value: [list.id] }], groups: [] }, list_context: list.id },
          fileName(list.name),
        ),
      { error: 'Exporteren mislukt.' },
    )
    setExporting(false)
  }

  async function copy() {
    const created = await runWithFeedback(() => duplicateList(list.id), { error: 'Dupliceren mislukt.' })
    if (!created) return
    toast.success(`Lijst '${created.name}' aangemaakt.`)
    void navigate(`/relaties/lijsten/${created.id}`)
  }

  async function makeStatic() {
    const ok = await confirm({
      title: `Lijst '${list.name}' omzetten naar statisch?`,
      description: 'De leden van nu worden vastgelegd. Het filter vervalt en de lijst volgt het filter niet meer.',
      confirmLabel: 'Omzetten',
      destructive: false,
    })
    if (!ok) return
    const updated = await runWithFeedback(() => listToStatic(list.id), { error: 'Omzetten mislukt.' })
    if (!updated) return
    toast.success('De lijst is nu statisch.')
    onChanged(updated)
  }

  async function toggleArchive() {
    const note = list.mailchimp_sync_enabled
      ? archived
        ? ' De Mailchimp-sync gaat weer lopen.'
        : ' De Mailchimp-sync stopt zolang de lijst gearchiveerd is.'
      : ''
    const ok = await confirm({
      title: archived ? `Lijst '${list.name}' terugzetten?` : `Lijst '${list.name}' archiveren?`,
      description: archived
        ? `De lijst komt weer in het overzicht en er kunnen weer leden bij.${note}`
        : `De lijst blijft bestaan maar staat niet meer in de tegels en er kunnen geen leden bij.${note}`,
      confirmLabel: archived ? 'Terugzetten' : 'Archiveren',
      destructive: false,
    })
    if (!ok) return
    const updated = await runWithFeedback(() => (archived ? unarchiveList(list.id) : archiveList(list.id)), {
      error: archived ? 'Terugzetten mislukt.' : 'Archiveren mislukt.',
    })
    if (!updated) return
    toast.success(archived ? 'Lijst teruggezet.' : 'Lijst gearchiveerd.')
    onChanged(updated)
  }

  async function remove() {
    const count = list.members_count
    const ok = await confirm({
      title: `Lijst '${list.name}' verwijderen?`,
      description: `${count === null ? 'De lijst' : `De lijst met ${contacts(count)}`} wordt verwijderd. De contacten zelf blijven bestaan.`,
      confirmLabel: 'Verwijderen',
      destructive: true,
    })
    if (!ok) return
    const done = await runWithFeedback(async () => {
      await deleteListV2(list.id)
      return true
    }, { error: 'Verwijderen mislukt.' })
    if (!done) return
    toast.success(`Lijst '${list.name}' verwijderd.`)
    void navigate('/relaties/lijsten')
  }

  const actions = (
    <>
      {isStatic && !archived ? (
        <Button asChild variant="outline">
          <Link to={`/relaties/importeren?modus=contacten&lijst=${list.id}`}>
            <Upload />
            Importeren
          </Link>
        </Button>
      ) : null}
      <Button type="button" variant="outline" disabled={exporting} onClick={() => void exportMembers()}>
        <Download />
        {exporting ? 'Bezig…' : 'Exporteren'}
      </Button>
      <Button type="button" variant="outline" onClick={() => setEditing(true)}>
        <Pencil />
        Bewerken
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="icon" aria-label="Meer acties">
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => void copy()}>Dupliceren</DropdownMenuItem>
          {!isStatic ? <DropdownMenuItem onSelect={() => void makeStatic()}>Omzetten naar statisch</DropdownMenuItem> : null}
          <DropdownMenuItem onSelect={() => void toggleArchive()}>{archived ? 'Terugzetten' : 'Archiveren'}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void remove()}>Verwijderen</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )

  return (
    <>
      <PageHeader title={list.name} actions={actions} />
      <section aria-label="Over deze lijst" className="vonk-card flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
          {list.kind ? <CategoryChip category={list.kind} /> : null}
          {when ? <span>{when}</span> : null}
          {list.location ? <span>{list.location}</span> : null}
          <span>{isStatic ? 'Statisch' : 'Dynamisch'}</span>
          {list.owner ? (
            <span className="inline-flex items-center gap-1.5">
              <EntityAvatar kind="person" name={list.owner.full_name} size="xs" />
              {list.owner.full_name}
            </span>
          ) : null}
          {archived ? <StatusBadge tone="neutral">Gearchiveerd</StatusBadge> : null}
        </div>
        {list.description_html ? <RichTextView html={list.description_html} /> : null}
        {list.filter_warning ? (
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <StatusBadge tone="warning">Let op</StatusBadge>
            <span>{list.filter_warning}</span>
          </p>
        ) : null}
      </section>
      <ListDialog open={editing} onOpenChange={setEditing} list={list} onSaved={onChanged} />
      {dialog}
    </>
  )
}
